import { EditIcon } from "@chakra-ui/icons";
import { Menu, MenuList, MenuItem, MenuDivider } from "@chakra-ui/react";
import { useState } from "react";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import MoreMenuButton from "components/Shared/MoreMenuButton";
import { getFragmentData, gql } from "__generated__/gql";
import EditPanelContent from "components/Products/EditPanelContent";
import DeleteProductLineMenuItem, {
  DeleteProductLineData,
} from "./DeleteProductLineMenuItem";
import ArchiveProductLineMenuItem, {
  OnArchiveSuccess,
} from "./ArchiveProductLineMenuItem";
import Edit from "./Edit";
import View from "./View";

const fragment = gql(/* GraphQL */ `
  fragment ProductLineFields on ProductLine {
    id
    name
    isArchived
    ...ProductLineView
    ...ProductLineEdit
    ...ArchiveProductLineButtonProductLine
    ...DeleteProductLineMenuItemProductLine
  }
`);

const productLineQuery = gql(/* GraphQL */ `
  query ProductLinePanel($productLineId: UUID!) {
    productLine(id: $productLineId) {
      id
      ...ProductLineFields
    }
  }
`);

type Props = {
  onArchiveSuccess?: OnArchiveSuccess;
  onClose: () => void;
  onDeleteSuccess?: (data: DeleteProductLineData) => void;
  productLineId?: string;
};

export default function ProductLinePanel({
  onArchiveSuccess,
  onClose,
  onDeleteSuccess,
  productLineId,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const { data, loading } = useBrandQuery(productLineQuery, {
    skip: !productLineId,
    variables: productLineId ? { productLineId } : undefined,
    returnPartialData: true,
  });

  const isLoading = loading || typeof window === "undefined";
  const productLine = getFragmentData(fragment, data?.productLine);

  return (
    <EditPanelContent
      onClose={onClose}
      description={isEditing ? "Edit Product Line" : "Product Line Details"}
      heading={productLine?.name}
      isArchived={productLine?.isArchived ?? undefined}
      isLoading={isLoading}
      headingControls={
        <Menu>
          <MoreMenuButton
            aria-label="Product Line Actions"
            size="md"
            px=".2em"
            py=".2em"
            isDisabled={isEditing}
          />
          <MenuList fontSize="md">
            <MenuItem
              icon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              isDisabled={!productLine?.id}
            >
              Edit Product Line Details…
            </MenuItem>

            <MenuDivider />

            <ArchiveProductLineMenuItem
              onArchiveSuccess={onArchiveSuccess}
              productLine={productLine}
            />

            <DeleteProductLineMenuItem
              productLine={productLine}
              onCompleted={onDeleteSuccess}
            />
          </MenuList>
        </Menu>
      }
    >
      {isEditing ? (
        <Edit
          productLine={productLine}
          isLoading={isLoading}
          onCancel={() => setIsEditing(false)}
          onComplete={() => setIsEditing(false)}
        />
      ) : (
        <View productLine={productLine} isLoading={isLoading} />
      )}
    </EditPanelContent>
  );
}
